import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import ReferralAnswerField from '../components/ReferralAnswerField';
import ContactPicker from '../components/ContactPicker';
import {
  Users, Phone, Mail, CheckSquare, ArrowRightLeft, Plus, Search,
  Filter, RefreshCw, X, Clock, AlertTriangle, CheckCircle, User,
  Building, ChevronRight, Calendar, MessageSquare, Eye, Edit2,
  Tag, FileText, Workflow, Play, Pause, Square, Trash2, Zap,
  Settings, LayoutList, Send, Shield, BarChart3, UserCog, BookOpen,
  Target, TrendingUp, TrendingDown, Award, DollarSign, Banknote,
  Brain, Radio, PhoneCall, MessageCircle, Wifi, Activity, Hash, Key, Bell, Globe,
  PieChart, GitMerge, FolderOpen, Upload, Download, Copy, Layers, ListFilter,
  CreditCard, Receipt, Star, Mic, PenTool, LayoutTemplate, GraduationCap, Webhook, Share2, Bot,
  Flame, UserCheck, Megaphone, FlaskConical, Sun,
  Briefcase, Speaker, Wrench, Package,
} from 'lucide-react';
import useArrowNav from '../hooks/useArrowNav';
import DraggableTabBar from '../components/DraggableTabBar';
import WorkspaceTabBar from '../components/WorkspaceTabBar';
// Task #94 (Jun 9 2026): workspaceTabs.js is now ESM (export keywords).
// The previous CJS form tripped webpack 5.106's strict CommonJsPlugin
// runtime check. Jest unit suite picks up the new ESM via babel-jest
// transform configured in jest.config.js for src/ files.
import * as wsTabs from '../lib/workspaceTabs';
import { readParam, writeParams } from '../lib/urlState';
import useBrowserBackStep from '../lib/browserBackStep';
import { useNavHistory } from '../lib/NavHistory';
import useUnsavedWarning from '../lib/useUnsavedWarning';
import GroupedTabNav from './crm/GroupedTabNav';
import TabErrorBoundary from './crm/TabErrorBoundary';
import PrintButton from '../components/PrintButton';

// ─── Shared Helpers & Constants ───────────────────────────────────────────
import {
  safeAsync, sp, Badge, KPI,
  neuShadow, neuShadowSm, neuShadowInset, neuShadowPressed,
  inp, btnPrimary, btnSm, btnDanger, cardWrap, emptyMsg,
  overlayStyle, modalStyle, modalHeader, modalTitle, closeBtn, neuCard, neuCardInner,
  CHANNEL_COLORS, STATUS_COLORS, GRADE_COLORS, DOC_TYPE_COLORS, APPT_STATUS_COLORS,
  OUTCOME_COLORS, CAMPAIGN_STATUS_COLORS, DOC_TYPES,
  CONTACT_TYPES, TASK_PRIORITY, STEP_ACTIONS,
} from './crm/crmShared';

// ─── Tab Components ──────────────────────────────────────────────────────
import PipelineTab from './crm/PipelineTab';
import ContactsTab from './crm/ContactsTab';
import InteractionsTab from './crm/InteractionsTab';
import TasksTab from './crm/TasksTab';
import ReferralsTab from './crm/ReferralsTab';
import ResearchTab from './crm/ResearchTab';
import SettingsGroup from './crm/SettingsGroup';
import TemplatesTab from './crm/TemplatesTab';
import SOPGroup from './crm/SOPGroup';
import LeadScoreTab from './crm/LeadScoreTab';
import DailyHitListTab from './crm/DailyHitListTab';
import CommsTab from './crm/CommsTab';
import ApiSettingsTab from './crm/ApiSettingsTab';
import AutomationTab from './crm/AutomationTab';
import LeadAssignmentTab from './crm/LeadAssignmentTab';
import SalesDashboardTab from './crm/SalesDashboardTab';
import MarketingDashboardTab from './crm/MarketingDashboardTab';
import OperationsDashboardTab from './crm/OperationsDashboardTab';
import AddTaskModal from './crm/AddTaskModal';
import BulkSendModal from './crm/BulkSendModal';
import SequencesTab from './crm/SequencesTab';
import AnalyticsTab from './crm/AnalyticsTab';
import TimelineTab from './crm/TimelineTab';
import CampaignsTab from './crm/CampaignsTab';
import DocsTab from './crm/DocsTab';
import CalendarTab from './crm/CalendarTab';
import OperationsCalendarTab from './crm/OperationsCalendarTab';
import FiltersTab from './crm/FiltersTab';
import BulkOpsTab from './crm/BulkOpsTab';
import DuplicatesTab from './crm/DuplicatesTab';
import SopTab from './crm/SopTab';
import SetupTab from './crm/SetupTab';
import UsersTab from './crm/UsersTab';
import OpportunitiesTab from './crm/OpportunitiesTab';
import ContactDetailView from './crm/ContactDetailView';
import { canUnlinkPatientContact } from '../components/PatientContactLink';
import ContactWorkspaceNavStep from './crm/ContactWorkspaceNavStep';
import CRMImportPage from './CRMImportPage';
import PaymentsTab from './crm/PaymentsTab';
import InvoicesTab from './crm/InvoicesTab';
import CatalogAdminTab from './crm/CatalogAdminTab';
import ReputationTab from './crm/ReputationTab';
import PhoneSystemTab from './crm/PhoneSystemTab';
import SignaturesTab from './crm/SignaturesTab';
import FunnelsTab from './crm/FunnelsTab';
import MembershipsTab from './crm/MembershipsTab';
import WebhooksTab from './crm/WebhooksTab';
import SocialPlannerTab from './crm/SocialPlannerTab';
import AIAssistantTab from './crm/AIAssistantTab';
import MedicalIntelligenceTab from './crm/MedicalIntelligenceTab';
import MyDayTab from './crm/MyDayTab';
import ContactDrawer from '../components/ContactDrawer';
import { contactOpenIntent } from '../lib/contactOpenIntent';
// CompetitiveIntelTab removed — standalone Competitive Intel page accessible from sidebar

// ─── Add Contact Modal ──────────────────────────────────────────────────

function AddContactModal({ users, onClose, onCreated }) {
  // Foundation #1 (May 7 2026): added dob, leadSource, medicalInterest,
  // nextFollowUpDate. lead_source is free-form attribution (Facebook ad,
  // podcast, Dr. Smith referral) — kept separate from the existing
  // referral_source enum which represents the medical referral path.
  const [form, setForm] = useState({ contactType: 'prospect', firstName: '', lastName: '', organization: '', title: '', email: '', phone: '', city: '', state: '', zip: '', referralSource: '', pipelineStage: 'new', assignedTo: '', notes: '', dob: '', leadSource: '', medicalInterest: '', nextFollowUpDate: '', referralAnswer: '', referralPartnerId: '' });
  const [saving, setSaving] = useState(false);
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const sanitize = (s) => typeof s === 'string' ? s.trim().replace(/[<>]/g, '') : s;

  async function submit(e) {
    e.preventDefault();
    if (!form.firstName || !form.lastName) return;
    // Card 27: the referral question is required — the selects carry
    // `required` so the browser blocks first; this is the belt.
    if (form.referralAnswer !== 'none' && form.referralAnswer !== 'partner') return;
    if (form.referralAnswer === 'partner' && !form.referralPartnerId) return;
    setSaving(true);
    try {
      const sanitizedForm = {
        ...form,
        referralPartnerId: form.referralAnswer === 'partner' ? parseInt(form.referralPartnerId, 10) : null,
        firstName: sanitize(form.firstName),
        lastName: sanitize(form.lastName),
        organization: sanitize(form.organization),
        title: sanitize(form.title),
        email: sanitize(form.email),
        phone: sanitize(form.phone),
        city: sanitize(form.city),
        state: sanitize(form.state),
        zip: sanitize(form.zip),
        notes: sanitize(form.notes),
      };
      const r = await window.zignagenixAPI.createCRMContact(sanitizedForm);
      if (r.error) throw new Error(r.message);
      onCreated(r);
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>Add Contact</h2>
          <button onClick={onClose} style={closeBtn}><X size={18} color="#ADB5BD" /></button>
        </div>
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Type *</label>
              <select value={form.contactType} onChange={e => up('contactType', e.target.value)} style={inp}>
                {Object.entries(CONTACT_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select></div>
            <ReferralAnswerField answer={form.referralAnswer} partnerId={form.referralPartnerId}
              onChange={({ answer, partnerId }) => setForm(f => ({ ...f, referralAnswer: answer, referralPartnerId: partnerId }))} />
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>First Name *</label>
              <input value={form.firstName} onChange={e => up('firstName', e.target.value)} required style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Last Name *</label>
              <input value={form.lastName} onChange={e => up('lastName', e.target.value)} required style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Organization</label>
              <input value={form.organization} onChange={e => up('organization', e.target.value)} style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Email</label>
              <input type="email" value={form.email} onChange={e => up('email', e.target.value)} style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Phone</label>
              <input value={form.phone} onChange={e => up('phone', e.target.value)} style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Referral Source</label>
              <select value={form.referralSource} onChange={e => up('referralSource', e.target.value)} style={inp}>
                <option value="">None</option>
                {['physician_referral','self_referral','web','walk_in','insurance','marketing','word_of_mouth','other'].map(s =>
                  <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
              </select></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Assigned To</label>
              <select value={form.assignedTo} onChange={e => up('assignedTo', e.target.value)} style={inp}>
                <option value="">Unassigned</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
              </select></div>
            {/* Foundation #1: lead lifecycle fields */}
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Date of Birth</label>
              <input type="date" value={form.dob} onChange={e => up('dob', e.target.value)} style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Lead Source</label>
              <input value={form.leadSource} onChange={e => up('leadSource', e.target.value)} placeholder="e.g. Facebook ad, podcast, referral" style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Medical Interest</label>
              <input value={form.medicalInterest} onChange={e => up('medicalInterest', e.target.value)} placeholder="e.g. stem cell, NK cell, exosome" style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Next Follow-Up</label>
              <input type="datetime-local" value={form.nextFollowUpDate} onChange={e => up('nextFollowUpDate', e.target.value)} style={inp} /></div>
          </div>
          <div style={{ marginBottom: 16 }}><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Notes</label>
            <textarea value={form.notes} onChange={e => up('notes', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical' }} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={btnSm}>Cancel</button>
            <button type="submit" disabled={saving} style={btnPrimary}>{saving ? 'Saving...' : 'Add Contact'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

function EditContactModal({ contact, users, onClose, onSaved }) {
  // Foundation #1: dob (DATE) → "YYYY-MM-DD"; next_follow_up_date (TIMESTAMPTZ)
  // → "YYYY-MM-DDTHH:MM" for datetime-local input. Both inputs return empty
  // string when cleared, which the backend coerces to NULL.
  const fmtDate = (v) => v ? String(v).slice(0, 10) : '';
  const fmtDateTime = (v) => v ? String(v).slice(0, 16) : '';
  const [form, setForm] = useState({
    contactType: contact.contact_type || 'prospect', firstName: contact.first_name || '', lastName: contact.last_name || '',
    organization: contact.organization || '', title: contact.title || '', email: contact.email || '',
    phone: contact.phone || '', fax: contact.fax || '', city: contact.city || '', state: contact.state || '', zip: contact.zip || '',
    referralSource: contact.referral_source || '', pipelineStage: contact.pipeline_stage || 'new',
    assignedTo: contact.assigned_to || '', notes: contact.notes || '',
    dob: fmtDate(contact.dob),
    leadSource: contact.lead_source || '',
    medicalInterest: contact.medical_interest || '',
    nextFollowUpDate: fmtDateTime(contact.next_follow_up_date),
  });
  const [saving, setSaving] = useState(false);
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.firstName || !form.lastName) return;
    setSaving(true);
    try {
      await window.zignagenixAPI.updateCRMContact(contact.id, form);
      onSaved();
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={overlayStyle}>
      <div style={modalStyle}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>Edit Contact</h2>
          <button onClick={onClose} style={closeBtn}><X size={18} color="#ADB5BD" /></button>
        </div>
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Type *</label>
              <select value={form.contactType} onChange={e => up('contactType', e.target.value)} style={inp}>
                {Object.entries(CONTACT_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>First Name *</label>
              <input value={form.firstName} onChange={e => up('firstName', e.target.value)} required style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Last Name *</label>
              <input value={form.lastName} onChange={e => up('lastName', e.target.value)} required style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Organization</label>
              <input value={form.organization} onChange={e => up('organization', e.target.value)} style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Email</label>
              <input type="email" value={form.email} onChange={e => up('email', e.target.value)} style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Phone</label>
              <input value={form.phone} onChange={e => up('phone', e.target.value)} style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Pipeline Stage</label>
              <input value={form.pipelineStage} onChange={e => up('pipelineStage', e.target.value)} style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Assigned To</label>
              <select value={form.assignedTo} onChange={e => up('assignedTo', e.target.value)} style={inp}>
                <option value="">Unassigned</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.display_name}</option>)}
              </select></div>
            {/* Foundation #1: lead lifecycle fields */}
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Date of Birth</label>
              <input type="date" value={form.dob} onChange={e => up('dob', e.target.value)} style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Lead Source</label>
              <input value={form.leadSource} onChange={e => up('leadSource', e.target.value)} placeholder="e.g. Facebook ad, podcast, referral" style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Medical Interest</label>
              <input value={form.medicalInterest} onChange={e => up('medicalInterest', e.target.value)} placeholder="e.g. stem cell, NK cell, exosome" style={inp} /></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Next Follow-Up</label>
              <input type="datetime-local" value={form.nextFollowUpDate} onChange={e => up('nextFollowUpDate', e.target.value)} style={inp} /></div>
          </div>
          <div style={{ marginBottom: 16 }}><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Notes</label>
            <textarea value={form.notes} onChange={e => up('notes', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical' }} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={btnSm}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, background: '#62BB46' }}>{saving ? 'Saving...' : 'Save Changes'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Log Interaction Modal ──────────────────────────────────────────────

function LogInteractionModal({ contacts, onClose, onCreated }) {
  const [form, setForm] = useState({ contactId: '', patientId: '', interactionType: 'phone_call', direction: 'outbound', subject: '', description: '', durationMinutes: '', outcome: '' });
  const [saving, setSaving] = useState(false);
  const up = (k, v) => setForm(f => ({ ...f, [k]: v }));

  async function submit(e) {
    e.preventDefault();
    if (!form.subject || (!form.contactId && !form.patientId)) return;
    setSaving(true);
    try {
      const r = await window.zignagenixAPI.createCRMInteraction(form);
      if (r.error) throw new Error(r.message);
      onCreated(r);
    } catch (err) { alert(err.message); }
    finally { setSaving(false); }
  }

  return (
    <div style={overlayStyle}>
      <div style={{ ...modalStyle, width: 480 }}>
        <div style={modalHeader}>
          <h2 style={modalTitle}>Log Interaction</h2>
          <button onClick={onClose} style={closeBtn}><X size={18} color="#ADB5BD" /></button>
        </div>
        <form onSubmit={submit}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Contact</label>
              <select value={form.contactId} onChange={e => up('contactId', e.target.value)} style={inp}>
                <option value="">Select...</option>
                {contacts.map(c => <option key={c.id} value={c.id}>{c.last_name}, {c.first_name}</option>)}
              </select></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Type</label>
              <select value={form.interactionType} onChange={e => up('interactionType', e.target.value)} style={inp}>
                {['phone_call','email','in_person','video_call','text_message','other'].map(t =>
                  <option key={t} value={t}>{t.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>)}
              </select></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Direction</label>
              <select value={form.direction} onChange={e => up('direction', e.target.value)} style={inp}>
                <option value="outbound">Outbound</option><option value="inbound">Inbound</option>
              </select></div>
            <div><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Duration (min)</label>
              <input type="number" value={form.durationMinutes} onChange={e => up('durationMinutes', e.target.value)} style={inp} /></div>
          </div>
          <div style={{ marginBottom: 12 }}><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Subject *</label>
            <input value={form.subject} onChange={e => up('subject', e.target.value)} required style={inp} /></div>
          <div style={{ marginBottom: 12 }}><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Description</label>
            <textarea value={form.description} onChange={e => up('description', e.target.value)} rows={2} style={{ ...inp, resize: 'vertical' }} /></div>
          <div style={{ marginBottom: 16 }}><label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', display: 'block', marginBottom: 3 }}>Outcome</label>
            <input value={form.outcome} onChange={e => up('outcome', e.target.value)} style={inp} /></div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={btnSm}>Cancel</button>
            <button type="submit" disabled={saving} style={{ ...btnPrimary, background: '#62BB46' }}>{saving ? 'Saving...' : 'Log Interaction'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Send Message Modal (global — usable from any CRM tab) ───────────────

function SendMessageModal({ contacts, sendChannel, setSendChannel, sendPrefill, setSendPrefill, sendResult, setSendResult, onClose, onSent }) {
  const [sending, setSending] = useState(false);
  /*
   * An SMS or email typed to a patient and not yet sent is unsaved PHI, and a
   * reload discarded it with no prompt. Boolean only — see the textarea below.
   */
  const [sendDraftDirty, setSendDraftDirty] = useState(false);
  useUnsavedWarning(sendDraftDirty, 'You have a message typed but not sent.');
  const sendToRef = useRef(null);  // to_address input, for auto-fill on contact pick
  // AI suggest-message: requests a draft from Claude (via Bedrock) using the
  // contact's recent interactions, score, and pipeline stage. Populates the
  // body textarea on success — user can edit before sending.
  const [suggesting, setSuggesting] = useState(false);
  const [intent, setIntent] = useState('follow_up');
  async function suggest() {
    const form = document.querySelector('form[data-send-form]');
    const fd = new FormData(form);
    const cid = parseInt(fd.get('contact_id'), 10);
    if (!cid) { alert('Pick a contact first.'); return; }
    setSuggesting(true);
    try {
      const r = await window.zignagenixAPI.aiSuggestMessage({
        contactId: cid, channel: sendChannel, intent,
      });
      if (!r.success) { alert(r.error || 'Suggestion failed'); return; }
      // Email returns "Subject: ...\n\nbody". Parse + populate both fields.
      let msg = r.message || '';
      if (sendChannel === 'email' && /^Subject:/im.test(msg)) {
        const lines = msg.split(/\n/);
        const subjectLine = lines.shift() || '';
        const subj = subjectLine.replace(/^Subject:\s*/i, '').trim();
        msg = lines.join('\n').replace(/^\s+/, '');
        const sub = form.querySelector('[name=subject]');
        if (sub) sub.value = subj;
      }
      const bodyEl = form.querySelector('[name=body]');
      if (bodyEl) bodyEl.value = msg;
    } finally { setSuggesting(false); }
  }
  const channelLabels = { sms: 'SMS', email: 'Email', whatsapp: 'WhatsApp', in_app: 'In-App', voice: 'Voice' };
  const channelColors = { sms: '#62BB46', email: '#2E8B98', whatsapp: '#62BB46', in_app: '#256F7A', voice: '#F26522' };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: '#F8F9FA', borderRadius: 20, boxShadow: '12px 12px 24px rgba(163,177,198,0.6), -12px -12px 24px rgba(255,255,255,0.9)', padding: 28, width: 520, maxHeight: '80vh', overflow: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#212529', margin: 0 }}>Send Message</h3>
          <button onClick={onClose} style={{ ...btnSm, padding: '4px 8px' }}><X size={14} /></button>
        </div>
        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          {['sms', 'email', 'whatsapp', 'in_app', 'voice'].map(c => (
            <button key={c} onClick={() => setSendChannel(c)}
              style={{ ...btnSm, background: sendChannel === c ? channelColors[c] : '#F8F9FA', color: sendChannel === c ? '#fff' : '#6C757D',
                boxShadow: sendChannel === c ? neuShadowInset : neuShadowSm, fontWeight: sendChannel === c ? 700 : 500 }}>
              {channelLabels[c]}
            </button>
          ))}
        </div>
        <form data-send-form key={`${sendChannel}-${sendPrefill?.contactId || ''}-${sendPrefill?.toAddress || ''}`} onSubmit={async (e) => {
          e.preventDefault();
          setSending(true); setSendResult(null);
          const fd = new FormData(e.target);
          // Scheduled-send branch (migration 044): when scheduled_at has a
          // value > 30s in the future, the backend queues the message and the
          // scheduler script (every 5 min) dispatches it. Empty = send now.
          const scheduledRaw = fd.get('scheduled_at');
          const scheduledIso = scheduledRaw ? new Date(scheduledRaw).toISOString() : null;
          try {
            const res = await window.zignagenixAPI.sendCRMMessage({
              contact_id: parseInt(fd.get('contact_id')),
              channel: sendChannel,
              to_address: fd.get('to_address') || '',
              subject: fd.get('subject') || '',
              body: fd.get('body') || '',
              scheduled_at: scheduledIso,
            });
            setSendResult(res);
            if (res.success) onSent();
          } catch (err) { setSendResult({ success: false, error: err.message }); }
          finally { setSending(false); }
        }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4, display: 'block' }}>Contact</label>
            <ContactPicker
              contacts={contacts}
              name="contact_id"
              defaultContactId={sendPrefill?.contactId || ''}
              inputStyle={inp}
              onSelect={(c) => {
                if (sendToRef.current) {
                  sendToRef.current.value = sendChannel === 'email' ? (c.email || '') : (c.phone || '');
                }
              }}
            />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4, display: 'block' }}>
              {sendChannel === 'email' ? 'Email Address' : sendChannel === 'in_app' ? 'Notification Title' : 'Phone Number'}
            </label>
            <input ref={sendToRef} name="to_address" defaultValue={sendPrefill?.toAddress || ''}
              placeholder={sendChannel === 'email' ? 'name@example.com' : sendChannel === 'in_app' ? '(optional)' : '+1 (555) 000-0000'}
              style={inp} required={sendChannel !== 'in_app'} />
          </div>
          {sendChannel === 'email' && (
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4, display: 'block' }}>Subject</label>
              <input name="subject" placeholder="Email subject line" style={inp} />
            </div>
          )}
          <div style={{ marginBottom: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D' }}>
                {sendChannel === 'voice' ? 'Voice Message (TwiML Say)' : 'Message Body'}
              </label>
              {/* AI suggest — only for SMS / email */}
              {(sendChannel === 'sms' || sendChannel === 'email') && (
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <select value={intent} onChange={e => setIntent(e.target.value)}
                    style={{ ...inp, width: 'auto', padding: '4px 8px', fontSize: 11 }} title="Intent the AI should write toward">
                    <option value="follow_up">Follow up</option>
                    <option value="post_consult">Post-consult</option>
                    <option value="appointment_reminder">Reminder</option>
                    <option value="re_engagement">Re-engage cold</option>
                    <option value="new_lead">Welcome new lead</option>
                  </select>
                  <button type="button" onClick={suggest} disabled={suggesting} style={{ ...btnSm, fontSize: 11, padding: '4px 10px' }}>
                    {suggesting ? 'Drafting…' : <><Bot size={12} /> Suggest</>}
                  </button>
                </div>
              )}
            </div>
            {/*
                ⚠ `onInput` reports LENGTH, never content — Card
                PARTNER-LIST-REFRESH. An SMS or email to a patient is PHI, so
                the draft is never stored and never leaves this element; the
                only thing that escapes is the boolean that lets
                useUnsavedWarning prompt before a reload throws it away.
                The field stays UNCONTROLLED (the submit handler reads it via
                FormData) so typing does not re-render the modal on every
                keystroke — making it controlled purely to observe emptiness
                would be a real cost for no extra information.
            */}
            <textarea name="body" rows={4} placeholder={sendChannel === 'voice' ? 'Text to be spoken to the recipient…' : 'Type your message…'}
              onInput={(e) => setSendDraftDirty(e.target.value.trim().length > 0)}
              style={{ ...inp, resize: 'vertical' }} required />
          </div>
          {/* Schedule for later (migration 044). Leave blank to send now.
              Voice and in_app aren't supported by the scheduler in v1, so
              the field is hidden for those channels. */}
          {(sendChannel === 'sms' || sendChannel === 'email') && (
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: '#6C757D', marginBottom: 4, display: 'block' }}>
                Send at <span style={{ fontWeight: 400, color: '#94A3B8' }}>· optional, leave blank for immediate</span>
              </label>
              <input name="scheduled_at" type="datetime-local" style={inp} />
            </div>
          )}
          {sendResult && (
            <div style={{ padding: '10px 14px', borderRadius: 10, marginBottom: 12,
              background: sendResult.success ? '#dcfce7' : '#fef2f2',
              color: sendResult.success ? '#166534' : '#991b1b', fontSize: 12 }}>
              {sendResult.success ? (
                sendResult.status === 'scheduled' ? (
                  <span>✓ Scheduled for <strong>{new Date(sendResult.scheduled_at).toLocaleString()}</strong>. The scheduler runs every 5 minutes.</span>
                ) : (
                  <span>✓ Message sent! Status: <strong>{sendResult.status}</strong></span>
                )
              ) : (
                <span>✗ Failed{sendResult.status ? ` (status: ${sendResult.status})` : ''}: {sendResult.error || `No error string returned — raw response: ${JSON.stringify(sendResult)}`}</span>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
            <button type="button" onClick={onClose} style={btnSm}>Cancel</button>
            <button type="submit" disabled={sending} style={btnPrimary}>
              {sending ? 'Sending…' : <><Send size={13} /> Send via {sendChannel.toUpperCase()}</>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// Main CRM Page
// ═══════════════════════════════════════════════════════════════════════════

function CRMPage({ user, onNavigate, activePage = 'crm', sessionNonce = '' }) {
  /*
   * ⚠ `activePage` and `sessionNonce` arrive from App.js (review round 4).
   * Both feed src/lib/browserBackStep.js below: the first so the entry left
   * behind names the page we are actually on, the second so a marker cannot be
   * inherited across a change of user. Defaulted, so a test that renders this
   * page without them still gets a working CRM — it simply pushes its own
   * entry instead of adopting, which is the safe direction.
   */
  // NAV-4 (issue #380): the SECTION tab is restored from the same per-user
  // localStorage record the contact workspace uses. Before this, the workspace
  // persisted and this did not — so after a global Back and a return the user
  // landed back INSIDE their last open contact with the section silently reset
  // to My Day. One of the two restored and the other did not, which is what made
  // it read as a glitch rather than a fresh start.
  //
  // Lazy initialiser, so localStorage is read once per mount rather than on
  // every render. `|| 'my_day'` covers a first-ever visit and a malformed value;
  // an id that is no longer in the nav is caught by the validation effect below,
  // NOT here — see the comment on loadSectionTab.
  //
  // ⚠ THE URL WINS OVER localStorage — Card PARTNER-LIST-REFRESH. `?tab=` is
  // present only when this browser was already here (a reload, or a link a
  // colleague pasted); localStorage is the per-user memory of "last time".
  // When they disagree the address bar is the more recent intent, and it is
  // the only one of the two that can carry a deep link. An unknown value from
  // either source is caught by the selectableTabIds effect below, not here.
  const [tab, setTab] = useState(
    () => readParam('tab') || wsTabs.loadSectionTab(user?.id || "anon") || 'my_day'
  );
  const crmTabsRef = useRef(null);
  const { onKeyDown: handleCrmTabKeyDown } = useArrowNav(crmTabsRef, 'button', { vertical: false, activateOnFocus: true });
  // Core data
  const [contacts, setContacts] = useState([]);
  const [interactions, setInteractions] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [summary, setSummary] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [taskFilter, setTaskFilter] = useState('');
  // Modals
  const [showAddContact, setShowAddContact] = useState(false);
  const [showLogInteraction, setShowLogInteraction] = useState(false);
  const [showAddTask, setShowAddTask] = useState(false);
  /*
    ⛔⛔ A CREATED TASK WAS INVISIBLE — Card CONTACT-PROFILE-INTAKE item 12.

    TasksTab owns its list (Task #230): it fetches via getCRMFilteredList in an
    effect keyed on `activeFilter`. AddTaskModal's onCreated called loadData(),
    which refreshes THIS component's `tasks` — a prop TasksTab does not read.
    So the create succeeded, the modal closed, and the list sat there unchanged
    until the rep happened to change a filter.

    ⚠ A counter, not a boolean and not loadData(). The effect must RE-RUN, and
    the only thing that re-runs an effect is a changed dependency; a boolean
    flipped to true stays true on the second task. Monotonic is the whole
    point.
  */
  const [taskListVersion, setTaskListVersion] = useState(0);
  const [showEditContact, setShowEditContact] = useState(false);
  const [editContact, setEditContact] = useState(null);
  // Extended
  const [tags, setTags] = useState([]);
  const [customFields, setCustomFields] = useState([]);
  const [pipelines, setPipelines] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [workflowRuns, setWorkflowRuns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [seeding, setSeeding] = useState(false);
  // SOP data
  const [staff, setStaff] = useState([]);
  const [scorecardMetrics, setScorecardMetrics] = useState([]);
  const [scorecardEntries, setScorecardEntries] = useState([]);
  const [escalations, setEscalations] = useState([]);
  const [pipelineRules, setPipelineRules] = useState([]);
  // Compensation
  const [compPlans, setCompPlans] = useState([]);
  const [compGlobals, setCompGlobals] = useState([]);
  const [compPayouts, setCompPayouts] = useState([]);
  // AI Lead Scoring
  const [leadScores, setLeadScores] = useState([]);
  const [leadWeights, setLeadWeights] = useState([]);
  const [trainingData, setTrainingData] = useState([]);
  // Multichannel Comms
  const [channels, setChannels] = useState([]);
  const [messages, setMessages] = useState([]);
  const [callLog, setCallLog] = useState([]);
  // Automation
  const [automationRules, setAutomationRules] = useState([]);
  const [automationLog, setAutomationLog] = useState([]);
  // API Settings
  const [apiSettings, setApiSettings] = useState([]);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showBulkSend, setShowBulkSend] = useState(false);
  const [sendChannel, setSendChannel] = useState('sms');
  const [sendingMsg, setSendingMsg] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendPrefill, setSendPrefill] = useState(null); // { contactId, toAddress }
  const [apiConfigEdit, setApiConfigEdit] = useState(null);
  const [crmNotifications, setCrmNotifications] = useState([]);
  // Analytics, Timeline, Campaigns, Documents, Calendar, Filters, Duplicates
  const [analytics, setAnalytics] = useState(null);
  const [timelineContactId, setTimelineContactId] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [savedFilters, setSavedFilters] = useState([]);
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [loadError, setLoadError] = useState(null);

  // Task #71 (Manny P1): Multi-tab contact workspace. State is the
  // workspaceTabs shape from src/lib/workspaceTabs.js. Persisted per-user
  // to localStorage so a browser refresh / new login restores the
  // workspace. We initialize from localStorage on first mount.
  const [workspace, setWorkspace] = useState(() => {
    const restored = wsTabs.loadFromLocalStorage(user?.id || "anon");
    /*
     * ⛔⛔ `?contact=` REOPENS THE RECORD THE USER WAS ON — Card
     * PARTNER-LIST-REFRESH. This is the half of the fix that matters on a
     * phone call: the workspace already survived a reload, but `activePage`
     * did not, so nobody ever got back here to see it.
     *
     * It goes through wsTabs.openTab, exactly like a click, so the tab is
     * created if absent and merely ACTIVATED if already open — a reload can
     * never duplicate a tab or reorder the strip.
     *
     * ⚠ ID ONLY, and no name. The tab strip renders `name`, which arrives when
     * the detail view loads; until then the strip shows the id. That is
     * deliberate — a name in the URL would be in the address bar, the history
     * and the next Referer header, which is precisely what CrmFilterBar
     * refuses to do with a CRM search for the same reason.
     */
    const wanted = readParam('contact');
    if (!wanted) return restored;
    return wsTabs.openTab(restored, { id: wanted });
  });
  // selectedContactId is now purely derived from workspace.activeId —
  // a single source of truth eliminates the dual-state sync bugs that
  // would otherwise creep in.
  const selectedContactId = workspace.activeId;

  // Persist workspace AND the section tab on every change — NAV-4.
  //
  // ⚠ ONE WRITER, ONE RECORD. The section tab rides in the same localStorage
  // entry as the contact workspace precisely so the two cannot diverge again.
  // Do NOT add a second effect (or a `persistSectionTab` helper) that writes the
  // same key — the two would clobber each other, and the resulting bug would look
  // exactly like the one NAV-4 fixes.
  //
  // `tab` is in the dependency array because a section change must be written;
  // omitting it is the whole defect, in miniature.
  useEffect(() => {
    wsTabs.persistToLocalStorage({ ...workspace, sectionTab: tab }, user?.id || "anon");
  }, [workspace, tab, user]);

  /*
   * ⚠ The dirty-form guard, read live. `useNavHistory()` returns a safe no-op
   * controller outside a provider, so a test that renders this page bare still
   * works — and `mayLeave` there answers "yes", which is the correct answer
   * when there is nothing registered to guard.
   */
  const { mayLeave: navMayLeave } = useNavHistory();

  /*
   * ⛔⛔ THE BROWSER'S OWN BACK BUTTON — ONE ENTRY FOR THE WHOLE WORKSPACE.
   *
   * Card PIPELINE-PLACE-AND-FOCUS, 10 Oct 2026. Measured on `main` @ d0cc155:
   * not one `popstate` listener and not one `pushState` call existed in src/,
   * public/, portal/, main.js or preload.js, and the only history writes were
   * `replaceState`, which creates no entry. Every Back control routed through
   * src/lib/NavHistory.js — the on-screen button, Alt+<-, Ctrl/Cmd+[, the
   * history dropdown, the Android hardware button — EXCEPT the one a person in
   * a browser actually reaches for. Observed: Back from an open contact landed
   * on about:blank. The app was gone.
   *
   * ⚠⚠ IT IS HELD HERE, AT THE WORKSPACE, NOT IN ContactWorkspaceNavStep
   * (review round 2). That component is keyed on the active contact id, and
   * `wsTabs.activateTab` switches between two OPEN contacts without closing
   * either — so a browser step living there remounted on every switch and
   * closed the contact that was just opened.
   *
   * ⚠ THE CALL ORDER IS NO LONGER LOAD-BEARING (it was, in round 3). The entry
   * left behind is made correct by `canonicalise()` below, which runs
   * synchronously immediately before the push and CLEARS `contact`/`ctab`
   * explicitly — so it does not matter whether this effect runs before or
   * after the URL writer. Round 3 relied on the ordering alone and round 4
   * measured two shapes it could not reach (a workspace restored from
   * localStorage, and a `?contact=` deep link, both of which open a record on
   * the first commit). The position is kept because it reads well; nothing
   * depends on it.
   *
   * `onBack` closes the ACTIVE tab, the same thing the in-view Back button and
   * the NavHistory step do, so every Back control lands in exactly one place.
   */
  useBrowserBackStep({
    active: selectedContactId != null,
    /*
     * ⛔⛔ ASK BEFORE DISCARDING UNSAVED WORK — review round 5, major item 2.
     *
     * Until browser Back was wired, pressing it LEFT THE DOCUMENT and
     * ContactDetailView's `beforeunload` warned about a half-written note.
     * The traversal is now same-document, so that warning never fires and this
     * handler was closing the record — and the note with it — unasked.
     * `mayLeave()` is the same question the in-view Back button, Alt+<-, the
     * history dropdown and the Android hardware button all ask.
     *
     * Returning FALSE tells the hook the person said no; it then puts the
     * history entry back. Restoring the URL keys is OURS to do, because only
     * this component knows what they should say.
     */
    onBack: () => {
      if (!navMayLeave()) {
        writeParams({ tab, contact: selectedContactId == null ? '' : String(selectedContactId) });
        return false;
      }
      setWorkspace((s2) => (s2.activeId != null ? wsTabs.closeTab(s2, s2.activeId) : s2));
      return true;
    },
    /*
     * ⛔⛔ WHOSE SESSION THIS IS — review round 4. A marker is adopted only when
     * it carries this exact value, so one user's entry cannot be inherited by
     * the next person to sign in in the same document.
     */
    sessionNonce,
    /*
     * ⛔⛔ WHAT THE ENTRY UNDERNEATH MUST SAY: "this page, nothing open."
     *
     * Run synchronously immediately before the push. Call order alone was not
     * enough (round 3's attempt): when a contact is opened on the FIRST commit
     * — a workspace restored from localStorage, or a `?contact=` deep link —
     * the push happens before App writes `?page=`, and a deep link already had
     * `contact=` in the URL before React ran at all. MEASURED: Back then landed
     * on `?page=settings` with the CRM on screen.
     *
     * `page` is written explicitly rather than relied upon, so this does not
     * depend on which effect ran first. `pipeline` and `stage` are deliberately
     * LEFT ALONE: they are part of "this page", not of the record being opened,
     * and clearing them would drop the board's place from the entry the person
     * comes back to.
     */
    canonicalise: () => writeParams({
      page: activePage || 'crm',
      tab,
      contact: '',
      ctab: '',
    }),
  });

  // Mirror the section tab + open contact into the URL so a reload returns
  // here. Separate effect from the localStorage writer ON PURPOSE: that one
  // owns ONE record and the comment above forbids a second writer to it. This
  // writes a different medium entirely and shares no key with it.
  useEffect(() => {
    writeParams({ tab, contact: selectedContactId == null ? '' : String(selectedContactId) });
  }, [tab, selectedContactId]);

  /*
   * ⛔⛔ AND CLEAR THEM WHEN THE CRM ITSELF GOES AWAY — review round 3.
   *
   * The effect above writes the CRM's keys while the CRM is on screen, and
   * nothing used to remove them when it left. MEASURED IN CHROME: with a
   * contact open, clicking Settings rendered `settings` while the address bar
   * still read `?page=crm&…&contact=5`, so a reload reopened the CRM contact
   * instead of the page the person was on. The same residue is what a sign-out
   * then had to race to clean.
   *
   * ⚠ UNMOUNT ONLY — an empty dependency list, NOT a cleanup on the effect
   * above. That one re-runs whenever `tab` or the open contact changes, and
   * clearing there would blank the address bar on every tab click.
   *
   * ⚠ `pipeline` and `stage` are cleared HERE rather than in PipelineTab, even
   * though PipelineTab writes them. PipelineTab unmounts every time a contact
   * is opened, so clearing there would drop the board's place from the URL for
   * exactly the round trip this card exists to preserve. The CRM page is the
   * right ownership boundary: these five keys live as long as it does.
   */
  useEffect(() => () => {
    writeParams({ tab: '', contact: '', ctab: '', pipeline: '', stage: '' });
  }, []);

  // ── List-state preservation (Daniel, 2026-06-10) ─────────────────────
  // Opening a contact used to unmount the list tab, destroying the search
  // term, filters, and scroll position — finishing with a record forced
  // the user to redo the whole search. Fix is two-part:
  //   1. The list tab stays MOUNTED but hidden while a contact is open
  //      (see the render section), so all its internal state survives.
  //   2. The list scroll position is captured when leaving the list and
  //      restored when the last contact tab closes.
  //
  // ⚠ FIXED (NAV-1): this used to read and write
  // `document.querySelector('.app-main').scrollTop`. `.app-main` is the OUTER
  // shell; this page is `height:100%; overflow:hidden` and its content scrolls
  // in the INNER `overflowY:'auto'` div below, so the shell's scrollTop is
  // effectively always 0. Capture ran, restore ran, and nothing was ever
  // restored — a silent no-op with no error to notice. The ref below is
  // attached to the element that actually scrolls, so the two halves now agree
  // on which node they mean. tests/react/CrmListScrollTarget.test.js guards it.
  const activeIdRef = useRef(workspace.activeId);
  const listScrollTopRef = useRef(0);
  const listScrollRef = useRef(null);
  const _captureListScroll = () => {
    if (activeIdRef.current == null && listScrollRef.current) {
      listScrollTopRef.current = listScrollRef.current.scrollTop;
    }
  };
  useEffect(() => {
    const wasOpen = activeIdRef.current != null;
    activeIdRef.current = selectedContactId;
    if (wasOpen && selectedContactId == null) {
      // Back to the list — restore scroll on the next frame, after the
      // hidden list re-flows to its full height.
      const y = listScrollTopRef.current;
      requestAnimationFrame(() => {
        if (listScrollRef.current) listScrollRef.current.scrollTop = y;
      });
    }
  }, [selectedContactId]);

  /*
   * ⛔⛔ THE CONTACT SIDE DRAWER, HOSTED HERE — Card P95.
   *
   * ⚠⚠ AT CRMPage LEVEL, NOT INSIDE EACH TAB, and that placement IS the
   * "one drawer at a time" requirement (item 3). A drawer owned by
   * PipelineTab and another owned by OpportunitiesTab are two drawers that
   * happen never to be mounted together today — a fact that holds only while
   * the tabs are mutually exclusive, which is not a property anybody is
   * guarding. One piece of state can only hold one contact.
   *
   * ⛔ AND IT IS WHY THE PIPELINE KEEPS ITS PLACE. The board stays MOUNTED
   * behind the slide-over, so its filters, its scroll position and #620's
   * place memory are not restored on close — they were never torn down.
   * openContactTab, by contrast, swaps the whole tab out for the contact
   * record, which is exactly the "staff lose their place" complaint.
   */
  const [drawerPreview, setDrawerPreview] = useState(null);

  const openContactTab = useCallback((id) => {
    if (id == null) return;
    _captureListScroll();
    setWorkspace(s => wsTabs.openTab(s, { id }));
  }, []);

  /**
   * Open the contact SIDE DRAWER — Card P95 items 1 and 2.
   *
   * ⛔⛔ A MODIFIER-CLICK STILL OPENS THE FULL RECORD (item 2), and it is
   * checked FIRST. cmd/ctrl-click is the gesture every browser has taught
   * people means "open this somewhere else and leave me where I am"; a
   * slide-over is not somewhere else. ⚠ Shift and middle-click are included
   * for the same reason — people reach for whichever one their habits give
   * them, and all three mean the same thing here.
   *
   * @param {number} id             contact id
   * @param {object} [opts]
   * @param {Event}  [opts.event]   the click, for the modifier check
   * @param {object} [opts.opportunity] the card this came from, if any — the
   *                 drawer needs it for Move stage and cannot infer it.
   */
  const openContactPreview = useCallback((id, opts = {}) => {
    if (id == null) return;
    if (contactOpenIntent(opts.event) === 'full') {
      openContactTab(id);
      return;
    }
    setDrawerPreview({ contactId: id, opportunity: opts.opportunity || null });
  }, [openContactTab]);

  /*
   * ⛔ Item 5 — the card behind refreshes, with NO full reload.
   *
   * ⚠ A loadData() here would be the easy answer and the wrong one: it
   * re-fetches every list the CRM holds and, on the pipeline, re-mounts the
   * board — which is precisely the lost place this card exists to stop. The
   * host updates the one opportunity it was told about and leaves everything
   * else, including scroll and filters, untouched.
   */
  const [stageMoves, setStageMoves] = useState({});
  const onDrawerStageMoved = useCallback(({ opportunityId, stage }) => {
    setStageMoves((m) => ({ ...m, [opportunityId]: stage }));
    setDrawerPreview((d) => (d ? { ...d, opportunity: d.opportunity ? { ...d.opportunity, stage } : null } : d));
  }, []);

  /*
   * ⛔⛔ AND THE OVERLAY MUST BE REMOVABLE — Card P95c item 2.
   *
   * It was write-only. A drawer move set { 7: 'proposal' }, and nothing ever
   * took it off. So dragging that card to Negotiation wrote negotiation on the
   * server and withStageMoves PUT IT BACK to Proposal on the board and in the
   * list — until CRMPage happened to remount. Worse, the next drop onto
   * Proposal then hit handleDrop's `dragItem.stage === stage` early return and
   * did nothing at all: the card was stuck, and the UI looked frozen.
   *
   * ⚠ The entry is DELETED, not set to the new value. The overlay exists to
   * cover the gap between a drawer move and the next load; once any other
   * writer has spoken for that row, the server is the better source and the
   * overlay has no business outliving it.
   */
  const clearStageMove = useCallback((opportunityId) => {
    if (opportunityId == null) return;
    setStageMoves((m) => {
      if (!(opportunityId in m)) return m;
      const next = { ...m };
      delete next[opportunityId];
      return next;
    });
  }, []);

  const closeContactTab = useCallback((id) => {
    setWorkspace(s => wsTabs.closeTab(s, id));
  }, []);

  const activateContactTab = useCallback((id) => {
    setWorkspace(s => wsTabs.activateTab(s, id));
  }, []);

  const closeAllTabs = useCallback(() => {
    setWorkspace(wsTabs.closeAll());
  }, []);

  /*
   * ⛔⛔ THE CRM'S TOP NAV, WITH A RECORD OPEN — Card P111 (B1/B2).
   *
   * ── THE MEASURED STATE THIS FIXES ─────────────────────────────────────────
   *
   * REPRODUCED by Cowork on prod, 24 Oct 2026, and the mechanism is three
   * lines of this file:
   *
   *   · the nav called `setTab` and NOTHING else;
   *   · the contact detail view renders whenever `selectedContactId` is set
   *     (below, "CONTACT DETAIL VIEW (full-screen overlay)");
   *   · and EVERY section tab body is gated `!selectedContactId && tab === …`.
   *
   * So with a contact open, clicking Pipeline — or Contacts, or Tasks, or any
   * of the ~40 others, CRM Admin included — changed `tab`, changed the URL,
   * suppressed the body that would have rendered, and left the overlay
   * exactly where it was. The screen did not move. Staff learned to press
   * "Back to List" first; three separate reviewers reported it as "the tabs
   * don't work".
   *
   * ── WHY DEACTIVATE AND NOT CLOSE ─────────────────────────────────────────
   *
   * `closeContactTab` was the tempting one-liner and it is the wrong verb.
   * The workspace exists so a rep can hold 5-10 records open through an
   * inbound call (Task #71); someone who clicks Pipeline has not asked to
   * throw one away. `wsTabs.deactivate` puts the record DOWN — the strip
   * stays, nothing is selected, one click returns to it.
   *
   * ── B2 FALLS OUT OF THIS, AND THAT IS THE POINT ──────────────────────────
   *
   * "Back sometimes lands on Invoices" was never a second bug. With the
   * overlay swallowing tab clicks, a person could click four tabs, see
   * nothing, then press Back — which closed the record and REVEALED whichever
   * tab they had last clicked blind. Invoices, if that was the last one.
   * Once a tab click is visible immediately there is no hidden drift left to
   * reveal, and Back unwinds the one step the open record registered
   * (ContactWorkspaceNavStep) — which this call removes, since the record is
   * no longer active. tests/react/CrmNavTabWithContactOpen.test.js drives
   * that sequence end to end rather than asserting it here.
   *
   * ⚠ THE DIRTY-FORM GUARD COMES FIRST. `navMayLeave()` is the same question
   * the in-view Back button, Alt+<-, the history dropdown and the browser
   * Back step all ask (see useBrowserBackStep above). A half-written note is
   * not discarded because somebody reached for the nav. On refusal we change
   * NOTHING — not the tab, not the workspace — so the person is left looking
   * at the record their unsaved work belongs to.
   *
   * ⚠ AND THE LIST SCROLL IS DROPPED ON PURPOSE. `listScrollRef` is the ONE
   * element that scrolls for every section, and the effect below restores
   * `listScrollTopRef` whenever the active record goes away. Without this
   * line, stepping off a contact onto Pipeline would scroll the BOARD to
   * wherever the contacts list had been left. A new section starts at the
   * top.
   */
  const changeSectionTab = useCallback((nextTab) => {
    if (nextTab == null || nextTab === tab) return;
    if (activeIdRef.current != null) {
      if (!navMayLeave()) return;
      listScrollTopRef.current = 0;
      setWorkspace(s => wsTabs.deactivate(s));
    }
    setTab(nextTab);
  }, [tab, navMayLeave]);


  // Legacy alias retained for any straggling internal callers — both
  // null (close all) and a single id (open + activate that tab).
  const setSelectedContactId = useCallback((idOrNull) => {
    if (idOrNull == null) {
      // Close just the currently active tab. (Matches the historical
      // single-contact "back" behavior. Other tabs stay open.)
      setWorkspace(s => s.activeId != null ? wsTabs.closeTab(s, s.activeId) : s);
    } else {
      _captureListScroll();
      setWorkspace(s => wsTabs.openTab(s, { id: idOrNull }));
    }
  }, []);

  // Task #55 (Jun 8 2026): sessionStorage handoff for cross-page deep
  // links. When the Patient detail page's "View in CRM" button navigates
  // here, it stashes the target contact id under this key. On CRM mount,
  // we consume the key (one-shot) and open that contact in the detail
  // view. sessionStorage is the right scope: it's tab-local, cleared on
  // tab close, never persisted across logins. Not a security control —
  // RBAC still gates the eventual fetch — but it cleanly avoids leaking
  // ids into the URL bar (no PHI in querystrings per HIPAA guidance).
  //
  // Task #63 also reads an optional `cowork.crm.openTab` key so the
  // Physician Dashboard can deep-link straight into the Physician sub-tab.
  const [pendingTabOnOpen, setPendingTabOnOpen] = useState(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem('cowork.crm.openContact');
      if (raw) {
        sessionStorage.removeItem('cowork.crm.openContact');
        const id = parseInt(raw, 10);
        if (Number.isFinite(id) && id > 0) {
          // Task #71: deep-link opens a tab so the multi-tab workspace
          // picks it up alongside any existing open tabs.
          setWorkspace(s => wsTabs.openTab(s, { id }));
        }
      }
      const tabRaw = sessionStorage.getItem('cowork.crm.openTab');
      if (tabRaw) {
        sessionStorage.removeItem('cowork.crm.openTab');
        setPendingTabOnOpen(String(tabRaw));
      }
    } catch { /* sessionStorage unavailable — ignore */ }
  }, []);

  const permissions = user?.permissions || [];
  const canCreate = permissions.includes('crm:create');
  const canUpdate = permissions.includes('crm:update');
  /*
   * Card P100b — the PATIENT-side permissions, which are not the CRM ones.
   * Creating a chart needs patients:create AND crm:update (the link writes to
   * the contact); linking an existing one needs patients:update AND
   * crm:update. The server checks both regardless — these only decide
   * whether a button is offered.
   */
  const canCreatePatient = permissions.includes('patients:create') && canUpdate;
  const canLinkPatient = permissions.includes('patients:update') && canUpdate;
  /*
   * ⛔ P121 — UNLINK IS A ROLE TEST, NOT A PERMISSION TEST.
   * routes/patient_contact_link.js:173 refuses on session.role, and
   * patients:update is held by six roles, four of which the ruling excludes.
   * Gating on the permission would offer Unlink to a nurse and a physician
   * and then refuse them at the server — a button that exists to fail.
   */
  const canUnlinkPatient = canUnlinkPatientContact(user);

  // ── Load all data ──────────────────────────────────────────────────────
  // Helper: unwrap a single result from window.zignagenixAPI.batch() into the
  // value the old per-call code expected. Logs + collects errors so the
  // existing loadError banner still surfaces the same information.
  function _unwrapBatchItem(r, label, errs) {
    if (!r) return null;
    if (!r.ok) {
      console.error(`[CRM] ${label} batch error:`, r.error);
      if (errs) errs.push(`${label}: ${r.error}`);
      return null;
    }
    if (r.value && r.value.error) {
      console.error(`[CRM] ${label} returned error:`, r.value);
      if (errs) errs.push(`${label}: ${r.value.message || r.value.code || 'unknown error'}`);
      return null;
    }
    return r.value;
  }

  const loadData = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      // PERF (Task #200): collapse 6 sequential round trips into 1 via
      // system:batch. Critical for offices far from us-east-1 (San Diego,
      // Cancun, Tijuana, Puerto Vallarta) where each round trip costs
      // 50-100ms. Server still runs auth + RBAC + audit per inner call.
      const items = [
        { channel: 'crm:contacts:list',     args: [{ search: search || undefined, contactType: typeFilter || undefined }] },
        { channel: 'crm:interactions:list', args: [{}] },
        { channel: 'crm:tasks:list',        args: [{ status: taskFilter || undefined }] },
        { channel: 'crm:referrals:list',    args: [{}] },
        { channel: 'crm:summary' },
      ];
      // Only request user list if the role has permission — server would
      // ACCESS_DENIED otherwise and pollute the audit log every page load.
      const wantsUsers = permissions.includes('users:read');
      if (wantsUsers) items.push({ channel: 'db:users:list' });

      const results = await window.zignagenixAPI.batch(items);
      const errors = [];
      const c = _unwrapBatchItem(results[0], 'Contacts',     errors);
      const i = _unwrapBatchItem(results[1], 'Interactions', errors);
      const t = _unwrapBatchItem(results[2], 'Tasks',        errors);
      const r = _unwrapBatchItem(results[3], 'Referrals',    errors);
      const s = _unwrapBatchItem(results[4], 'Summary',      null);
      const u = wantsUsers ? _unwrapBatchItem(results[5], 'Users', null) : [];
      if (errors.length > 0) setLoadError(errors.join(' | '));

      setContacts(Array.isArray(c) ? c : []);
      setInteractions(Array.isArray(i) ? i : []);
      setTasks(Array.isArray(t) ? t : []);
      setReferrals(Array.isArray(r) ? r : []);
      if (s) setSummary(s);
      setAllUsers(Array.isArray(u) ? u : []);
    } catch (err) { console.error('[CRM] loadData error:', err); setLoadError(err.message || 'Failed to load CRM data'); }
    finally { setLoading(false); }
  }, [search, typeFilter, taskFilter, permissions]);

  const loadExtended = useCallback(async () => {
    try {
      // PERF (Task #200): collapse 30 sequential round trips into 1 via
      // system:batch. Identical per-handler auth + RBAC + audit as before;
      // wall-clock load time for offices far from us-east-1 drops ~70%.
      const channels = [
        'crm:tags:list',
        'crm:custom-fields:list',
        'crm:pipelines:list',
        'crm:workflows:list',
        'crm:workflow-runs:list',
        'crm:templates:list',
        'crm:staff:list',
        'crm:scorecard:metrics-list',
        'crm:scorecard:entries-list',
        'crm:escalations:list',
        'crm:pipeline-rules:list',
        'crm:comp:plans-list',
        'crm:comp:globals-list',
        'crm:comp:payouts-list',
        'crm:lead-score:scores-list',
        'crm:lead-score:weights-list',
        'crm:lead-score:training-data',
        'crm:channels:list',
        'crm:messages:list',
        'crm:calls:list',
        'crm:automation:list',
        'crm:automation:log-list',
        'crm:api-settings:list',
        'crm:notifications:list',
        'crm:analytics:dashboard',
        'crm:campaigns:list',
        'crm:documents:list',
        'crm:appointments:list',
        'crm:filters:list',
        'crm:duplicates:list',
      ];
      // Channels with filter arg get {} so the server's runSql binding is
      // happy. Order matters — this set follows the destructuring below.
      const NEEDS_ARG = new Set([
        'crm:workflow-runs:list', 'crm:scorecard:entries-list',
        'crm:comp:payouts-list', 'crm:messages:list', 'crm:calls:list',
        'crm:automation:log-list', 'crm:notifications:list',
        'crm:analytics:dashboard', 'crm:documents:list', 'crm:appointments:list',
      ]);
      const items = channels.map(channel =>
        NEEDS_ARG.has(channel) ? { channel, args: [{}] } : { channel }
      );

      const results = await window.zignagenixAPI.batch(items);
      // Same {ok, value} envelope as loadData — null out failures, keep the
      // Array.isArray(...) ? ... : [] guards downstream so no consumer sees
      // an unexpected shape.
      const v = results.map(r => (r && r.ok ? r.value : null));
      const [tg, cf, pl, wf, wr, tm, st, sm, se, esc, pr, cp, cg, cpy, ls, lw, td, ch, ms, cl, ar, al, apis, notifs, anl, cmpgns, docs, appts, fltrs, dups] = v;

      setTags(Array.isArray(tg) ? tg : []);
      setCustomFields(Array.isArray(cf) ? cf : []);
      setPipelines(Array.isArray(pl) ? pl : []);
      setWorkflows(Array.isArray(wf) ? wf : []);
      setWorkflowRuns(Array.isArray(wr) ? wr : []);
      setTemplates(Array.isArray(tm) ? tm : []);
      setStaff(Array.isArray(st) ? st : []);
      setScorecardMetrics(Array.isArray(sm) ? sm : []);
      setScorecardEntries(Array.isArray(se) ? se : []);
      setEscalations(Array.isArray(esc) ? esc : []);
      setPipelineRules(Array.isArray(pr) ? pr : []);
      setCompPlans(Array.isArray(cp) ? cp : []);
      setCompGlobals(Array.isArray(cg) ? cg : []);
      setCompPayouts(Array.isArray(cpy) ? cpy : []);
      setLeadScores(Array.isArray(ls) ? ls : []);
      setLeadWeights(Array.isArray(lw) ? lw : []);
      setTrainingData(Array.isArray(td) ? td : []);
      setChannels(Array.isArray(ch) ? ch : []);
      setMessages(Array.isArray(ms) ? ms : []);
      setCallLog(Array.isArray(cl) ? cl : []);
      setAutomationRules(Array.isArray(ar) ? ar : []);
      setAutomationLog(Array.isArray(al) ? al : []);
      setApiSettings(Array.isArray(apis) ? apis : []);
      setCrmNotifications(Array.isArray(notifs) ? notifs : []);
      if (anl) setAnalytics(anl);
      setCampaigns(Array.isArray(cmpgns) ? cmpgns : []);
      setDocuments(Array.isArray(docs) ? docs : []);
      setAppointments(Array.isArray(appts) ? appts : []);
      setSavedFilters(Array.isArray(fltrs) ? fltrs : []);
      setDuplicateGroups(Array.isArray(dups) ? dups : []);
    } catch (err) { console.error('loadExtended error:', err); }
  }, []);

  useEffect(() => { loadData(); loadExtended(); }, [loadData, loadExtended]);

  async function completeTask(taskId) {
    await window.zignagenixAPI.completeCRMTask(taskId);
    loadData();
  }

  // ── Seed helpers ───────────────────────────────────────────────────────
  async function seedAll() {
    setSeeding(true);
    try {
      const r1 = await window.zignagenixAPI.seedDefaultTags();
      const r2 = await window.zignagenixAPI.seedDefaultCustomFields();
      const r3 = await window.zignagenixAPI.seedDefaultPipeline();
      const r4 = await window.zignagenixAPI.seedAllWorkflows();
      const r5 = await window.zignagenixAPI.seedDefaultStaff();
      const r6 = await window.zignagenixAPI.seedDefaultScorecard();
      const r7 = await window.zignagenixAPI.seedDefaultEscalations();
      const r8 = await window.zignagenixAPI.seedDefaultPipelineRules();
      const r9 = await window.zignagenixAPI.seedDefaultComp();
      const r10 = await window.zignagenixAPI.seedLeadScoreWeights();
      const r11 = await window.zignagenixAPI.seedDefaultChannels();
      const r12 = await window.zignagenixAPI.seedDefaultAutomation();
      const msg = `Seeded: ${r1.created} tags, ${r2.created} fields, pipeline ${r3.existed ? '(exists)' : 'created'}, ${r4.created} workflows, ${r5.created} staff, ${r6.created} metrics, ${r7.created} escalations, ${r8.created} rules, ${r9.globals} globals + ${r9.plans} comp plans, ${r10.created} score weights, ${r11.created} channels, ${r12.created} automation rules`;
      alert(msg);
      await loadExtended();
    } catch (err) { alert('Seed error: ' + err.message); }
    finally { setSeeding(false); }
  }

  async function toggleWorkflow(id, active) {
    await window.zignagenixAPI.toggleCRMWorkflow(id, active);
    loadExtended();
  }

  async function executeWorkflow(wfId, contactId) {
    try {
      const r = await window.zignagenixAPI.executeCRMWorkflow(wfId, contactId);
      // ⛔ Card P85: this line used to say "12 steps" when 4 of them were
      // texts and emails that were never sent. `summary` comes from the
      // server's own step log and names the skips explicitly.
      alert(`Workflow run — Status: ${r.status}\n${r.summary || `${r.stepsExecuted} steps completed`}`);
      loadExtended();
      loadData();
    } catch (err) { alert('Execute error: ' + err.message); }
  }

  async function stopRun(runId) {
    await window.zignagenixAPI.stopCRMWorkflowRun(runId);
    loadExtended();
  }

  // ── Shared props for all tab components ────────────────────────────────
  const sharedProps = {
    tab, contacts, interactions, tasks, referrals, summary, allUsers, loading,
    search, setSearch, typeFilter, setTypeFilter, taskFilter, setTaskFilter,
    showAddContact, setShowAddContact, showLogInteraction, setShowLogInteraction,
    showAddTask, setShowAddTask, taskListVersion, showEditContact, setShowEditContact,
    editContact, setEditContact,
    tags, customFields, pipelines, workflows, workflowRuns, templates,
    seeding, setSeeding, staff, scorecardMetrics, scorecardEntries,
    escalations, pipelineRules, compPlans, compGlobals, compPayouts,
    leadScores, leadWeights, trainingData,
    channels, messages, callLog, automationRules, automationLog,
    apiSettings, setApiSettings, showSendModal, setShowSendModal,
    showBulkSend, setShowBulkSend,
    sendChannel, setSendChannel, sendingMsg, setSendingMsg,
    sendResult, setSendResult, sendPrefill, setSendPrefill,
    apiConfigEdit, setApiConfigEdit,
    crmNotifications, setCrmNotifications,
    analytics, setAnalytics, timelineContactId, setTimelineContactId,
    timeline, setTimeline, campaigns, setCampaigns,
    documents, setDocuments, appointments, setAppointments,
    savedFilters, setSavedFilters, duplicateGroups, setDuplicateGroups,
    selectedContacts, setSelectedContacts,
    selectedContactId, onContactClick: openContactTab,
    /*
     * ⚠ A SEPARATE PROP, NOT A REDEFINED onContactClick. The Contacts tab and
     * the hit list still open the full record, which is right for them — a
     * drawer over a contact list is a list of contacts with a contact on top
     * of it. Only the boards that staff navigate BY, and lose their place in,
     * get the preview.
     */
    onContactPreview: openContactPreview,
    // ⚠ id → new stage, applied by the boards without a refetch (item 5).
    stageMoves,
    // ⚠ and removed by any other writer — see clearStageMove.
    clearStageMove,
    permissions, canCreate, canUpdate, loadData, loadExtended,
    completeTask, seedAll, toggleWorkflow, executeWorkflow, stopRun,
    // Task #203 — passed down so PipelineTab + PipelineIntelligence
    // components don't each fire their own getOwnProfile round trip.
    userRole: user?.role || 'unknown',
    // Task #230 — CrmFilterBar "My Records" shortcut + owner default
    currentUser: user || null,
  };

  // ── Tab config ─────────────────────────────────────────────────────────
  const TABS = [
    { id: 'my_day', label: 'My Day', icon: Sun },
    { id: 'pipeline', label: 'Pipeline', icon: LayoutList },
    { id: 'daily_hit_list', label: 'Daily Hit List', icon: Flame },
    { id: 'opportunities', label: 'Opportunities', icon: TrendingUp },
    { id: 'contacts', label: 'Contacts', icon: User },
    { id: 'users', label: 'ZignaGenix Staff & Team', icon: UserCog },
    { id: 'interactions', label: 'Conversations', icon: MessageSquare },
    { id: 'tasks', label: 'Tasks', icon: CheckSquare },
    { id: 'referrals', label: 'Referrals', icon: ArrowRightLeft },
    { id: 'research', label: 'Research', icon: FlaskConical },
    { id: 'tags', label: 'Tags', icon: Tag },
    { id: 'fields', label: 'Custom Fields', icon: FileText },
    { id: 'workflows', label: 'Workflows', icon: Zap },
    { id: 'templates', label: 'Templates', icon: Send },
    { id: 'scorecard', label: 'Scorecard', icon: BarChart3 },
    { id: 'escalations', label: 'Escalations', icon: Shield },
    { id: 'lead_score', label: 'AI Scoring', icon: Brain },
    { id: 'comms', label: 'Communications', icon: Radio },
    { id: 'automation', label: 'Automation', icon: Activity },
    { id: 'lead_assignment', label: 'Lead Assignment', icon: UserCheck },
    { id: 'comp', label: 'Compensation', icon: DollarSign },
    { id: 'sales_dashboard', label: 'Sales Dashboard', icon: TrendingUp },
    { id: 'marketing_dashboard', label: 'Marketing Dashboard', icon: Megaphone },
    { id: 'operations_dashboard', label: 'Operations Dashboard', icon: Activity },
    { id: 'analytics', label: 'Analytics', icon: PieChart },
    { id: 'timeline_tab', label: 'Timeline', icon: Clock },
    { id: 'campaigns', label: 'Campaigns', icon: Mail },
    { id: 'sequences', label: 'Sequences', icon: Workflow },
    { id: 'docs', label: 'Documents', icon: FolderOpen },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
    { id: 'ops_calendar', label: 'Operations Calendar', icon: Calendar },
    { id: 'filters', label: 'Smart Lists', icon: ListFilter },
    { id: 'bulk', label: 'Bulk Ops', icon: Layers },
    { id: 'duplicates', label: 'Duplicates', icon: GitMerge },
    { id: 'api_settings', label: 'API Settings', icon: Key },
    { id: 'sop', label: 'SOP', icon: BookOpen },
    { id: 'payments', label: 'Payments', icon: CreditCard },
    { id: 'invoices', label: 'Invoices', icon: Receipt },
    { id: 'reputation', label: 'Reputation', icon: Star },
    { id: 'phone_system', label: 'Phone System', icon: PhoneCall },
    { id: 'signatures', label: 'E-Signatures', icon: PenTool },
    // ⛔ custom_objects REMOVED from TABS TOO, 2026-10-09, and it had to be.
    // The inventory calls TABS unread (§10.10) and that is true of the
    // RENDER path — but it is NOT unread by the invariant:
    // tests/react/CrmSectionTabPersistence.test.js:137 asserts every TABS id
    // is reachable in GROUPS, because TABS is the flat deep-link / persisted
    // -state surface. Hiding the tab from GROUPS alone broke that gate, which
    // is how this was caught. A hidden tab must leave BOTH lists together.
    { id: 'funnels', label: 'Funnels & Forms', icon: LayoutTemplate },
    { id: 'memberships', label: 'Memberships', icon: GraduationCap },
    { id: 'webhooks', label: 'Webhooks', icon: Webhook },
    { id: 'social', label: 'Social Planner', icon: Share2 },
    { id: 'medical_intel', label: 'Medical Intelligence', icon: Brain },
    { id: 'ai_assistant', label: 'AI Assistant', icon: Bot },
    { id: 'setup', label: 'Setup', icon: Settings },
    { id: 'import', label: 'Import/Upload', icon: Upload },
  ];

  const tabStyle = (t2) => ({
    padding: '8px 14px', border: 'none', borderRadius: 10, margin: '2px 1px',
    background: tab === t2 ? '#F8F9FA' : 'transparent',
    boxShadow: tab === t2 ? neuShadow : 'none',
    cursor: 'pointer', fontSize: 12, fontWeight: tab === t2 ? 600 : 400,
    color: tab === t2 ? '#2E8B98' : '#6C757D', display: 'flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap',
    transition: 'all 0.15s ease',
  });

  // ── Grouped nav structure ──────────────────────────────────────────────
  // Same 49 tab keys as TABS above (so deep links and persisted state still
  // resolve), reorganized into 7 top-level categories. Sub-tab order within
  // each group follows the user-mental-model "daily → less frequent → admin".
  const GROUPS = [
    { id: 'my_day', label: 'My Day', icon: Sun },
    {
      id: 'pipeline_group', label: 'Pipeline', icon: LayoutList,
      tabs: [
        { id: 'pipeline',       label: 'Pipeline',        icon: LayoutList },
        { id: 'daily_hit_list', label: 'Daily Hit List',  icon: Flame },
        { id: 'opportunities',  label: 'Opportunities',   icon: TrendingUp },
        { id: 'tasks',          label: 'Tasks',           icon: CheckSquare },
        { id: 'calendar',       label: 'Calendar',        icon: Calendar },
        { id: 'ops_calendar',   label: 'Ops Calendar',    icon: Calendar },
        { id: 'timeline_tab',   label: 'Timeline',        icon: Clock },
      ],
    },
    {
      id: 'contacts_group', label: 'Contacts', icon: User,
      tabs: [
        { id: 'contacts',   label: 'Contacts',      icon: User },
        { id: 'referrals',  label: 'Referrals',     icon: ArrowRightLeft },
        { id: 'duplicates', label: 'Duplicates',    icon: GitMerge },
        { id: 'tags',       label: 'Tags',          icon: Tag },
        { id: 'fields',     label: 'Custom Fields', icon: FileText },
        { id: 'filters',    label: 'Smart Lists',   icon: ListFilter },
        { id: 'bulk',       label: 'Bulk Ops',      icon: Layers },
      ],
    },
    {
      id: 'outreach_group', label: 'Outreach', icon: MessageSquare,
      tabs: [
        { id: 'interactions', label: 'Conversations',  icon: MessageSquare },
        { id: 'templates',    label: 'Templates',      icon: Send },
        { id: 'sequences',    label: 'Sequences',      icon: Workflow },
        { id: 'campaigns',    label: 'Campaigns',      icon: Mail },
        { id: 'phone_system', label: 'Phone System',   icon: PhoneCall },
        { id: 'comms',        label: 'Communications', icon: Radio },
        { id: 'signatures',   label: 'E-Signatures',   icon: PenTool },
        { id: 'docs',         label: 'Documents',      icon: FolderOpen },
      ],
    },
    {
      id: 'marketing_group', label: 'Marketing', icon: Megaphone,
      tabs: [
        { id: 'funnels',     label: 'Funnels & Forms', icon: LayoutTemplate },
        { id: 'memberships', label: 'Memberships',     icon: GraduationCap },
        { id: 'social',      label: 'Social Planner',  icon: Share2 },
        { id: 'reputation',  label: 'Reputation',      icon: Star },
        { id: 'automation',  label: 'Automation',      icon: Activity },
        { id: 'workflows',   label: 'Workflows',       icon: Zap },
      ],
    },
    {
      id: 'intel_group', label: 'Dashboards & Intelligence', icon: BarChart3,
      tabs: [
        { id: 'sales_dashboard',      label: 'Sales Dashboard',      icon: TrendingUp },
        { id: 'marketing_dashboard',  label: 'Marketing Dashboard',  icon: Megaphone },
        { id: 'operations_dashboard', label: 'Operations Dashboard', icon: Activity },
        { id: 'analytics',            label: 'Analytics',            icon: PieChart },
        { id: 'scorecard',            label: 'Scorecard',            icon: BarChart3 },
        { id: 'lead_score',           label: 'AI Scoring',           icon: Brain },
        { id: 'medical_intel',        label: 'Medical Intelligence', icon: Brain },
        { id: 'research',             label: 'Research',             icon: FlaskConical },
        { id: 'ai_assistant',         label: 'AI Assistant',         icon: Bot },
      ],
    },
    {
      id: 'admin_group', label: 'Admin', icon: Settings,
      tabs: [
        { id: 'catalog',         label: 'Product Catalog',  icon: Package },
        { id: 'users',           label: 'Staff & Team',     icon: UserCog },
        { id: 'lead_assignment', label: 'Lead Assignment',  icon: UserCheck },
        { id: 'comp',            label: 'Compensation',     icon: DollarSign },
        { id: 'escalations',     label: 'Escalations',      icon: Shield },
        { id: 'payments',        label: 'Payments',         icon: CreditCard },
        { id: 'invoices',        label: 'Invoices',         icon: Receipt },
        { id: 'sop',             label: 'SOP',              icon: BookOpen },
        { id: 'api_settings',    label: 'API Settings',     icon: Key },
        // ⛔ CUSTOM OBJECTS HIDDEN 2026-10-09 (inventory 2026-10-08 §10.2).
        // NOT "coming soon" — the tab calls REST endpoints that do not exist
        // (src/pages/crm/CustomObjectsTab.js:49-94 fetch /api/crm/custom-objects;
        // no Express route serves that path), and it is written to swallow the
        // SPA's index.html fallback and render an EMPTY STATE. So it reads as
        // "no custom objects yet" when it is really "this cannot work" — the
        // worst of both, because nobody reports it as broken.
        //
        // ⚠ The backend is CLOSER than the inventory implies and this is worth
        // knowing before anyone rebuilds it: IPC handlers DO exist
        // (routes/crm.js:12241-12280, crm:custom-objects:list/create/update/
        // delete + crm:custom-records:*). What is missing is the REST route AND
        // a preload bridge method — grep preload.js for custom-objects: nothing.
        // Re-enabling is a transport fix, not a feature build. Out of scope here
        // by instruction: this card hides the dead surface, it does not build it.
        //
        // Safe to remove from GROUPS: selectableTabIds (:1224) is derived FROM
        // GROUPS, and the effect at :1244 sends any persisted-but-unselectable
        // tab back to my_day — so a user whose last tab was custom_objects lands
        // on My Day rather than a blank pane. Verified: the id is deep-linked
        // nowhere (no URL param, no onNavigate caller, no other module).
        { id: 'webhooks',        label: 'Webhooks',         icon: Webhook },
        { id: 'setup',           label: 'Setup',            icon: Settings },
        { id: 'import',          label: 'Import / Upload',  icon: Upload },
      ],
    },
  ];

  // ── NAV-4: a restored section tab must actually exist in the nav ─────────
  //
  // Validated against GROUPS rather than against a list in workspaceTabs.js,
  // because GROUPS is what RENDERS the nav — so it is the only definition that
  // can be wrong in a way the user sees. Two consequences worth stating:
  //
  //   1. `TABS` (declared just above GROUPS) is DEAD CODE — nothing reads it. Its
  //      comment claims GROUPS carries "the same 49 tab keys… so deep links and
  //      persisted state still resolve", and that has already drifted: `catalog`
  //      is reachable in GROUPS and absent from TABS. Validating against TABS
  //      would therefore have rejected a tab the user can legitimately be on.
  //   2. A container entry (one WITH a `tabs` array) is a group header, not a
  //      selectable tab, so `pipeline_group` and friends are excluded. A
  //      top-level entry WITHOUT `tabs` — `my_day` — IS selectable.
  //
  // This self-corrects rather than trusting storage: a tab REMOVED from the nav
  // in a future release, or a hand-edited localStorage value, falls back to
  // My Day instead of rendering a blank pane under a highlighted-nothing nav.
  const selectableTabIds = useMemo(() => {
    const out = new Set();
    for (const g of GROUPS) {
      if (Array.isArray(g.tabs)) for (const t of g.tabs) out.add(t.id);
      else out.add(g.id);
    }
    return out;
    // Empty deps ON PURPOSE: GROUPS is a static literal rebuilt on every render,
    // so listing it would rebuild this Set every render and re-fire the effect
    // below forever. Its id set never varies, so the first render's copy is
    // correct for the component's lifetime.
    //
    // ⚠ NO `eslint-disable` comment here. `react-hooks/exhaustive-deps` is NOT a
    // configured rule in this project — only `rules-of-hooks` is (the CI job of
    // that name) — and disabling an UNDEFINED rule is itself a build ERROR:
    // "Definition for rule 'react-hooks/exhaustive-deps' was not found". Unit and
    // react tests pass either way, so `npm run build` is the only gate that
    // catches it. It caught this.
  }, []);
  useEffect(() => {
    if (!selectableTabIds.has(tab)) setTab('my_day');
  }, [tab, selectableTabIds]);

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <div className="page" style={{ padding: 24, background: '#e0e5ec', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header (sticky) */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 40, height: 40, borderRadius: 10, background: 'linear-gradient(135deg, #2E8B98, #be185d)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Users size={22} color="#fff" />
          </div>
          <div>
            <h1 style={{ fontSize: 20, fontWeight: 700, margin: 0, color: '#212529' }}>ZignaGenix CRM</h1>
            <p style={{ fontSize: 12, color: '#6C757D', margin: 0 }}>Patient journey pipeline, workflows, tags & communications</p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Loading status pill — gives users a visible signal that data is
              in flight. Especially important for offices far from us-east-1
              (San Diego, Cancun, Tijuana) where the round trip can take a
              second or two. Without this, users click refresh and think
              nothing happened. */}
          {loading && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              fontSize: 12, color: '#6C757D', fontWeight: 500,
              padding: '4px 10px', background: '#F0F4F8', borderRadius: 12,
            }}>
              <RefreshCw size={12} style={{ animation: 'spin 0.8s linear infinite' }} />
              Loading CRM data…
            </span>
          )}
          <PrintButton pageTitle="ZignaGenix CRM" subtitle="Patient Journey Pipeline & Workflows" />
          <button
            onClick={() => { loadData(); loadExtended(); }}
            disabled={loading}
            title={loading ? 'Loading in progress…' : 'Refresh CRM data'}
            style={{
              ...btnSm,
              opacity: loading ? 0.5 : 1,
              cursor: loading ? 'wait' : 'pointer',
            }}
          >
            <RefreshCw
              size={14}
              color="#6C757D"
              style={loading ? { animation: 'spin 0.8s linear infinite' } : undefined}
            />
          </button>
        </div>
      </div>

      {/* Error Banner (sticky) */}
      {loadError && (
        <div style={{
          background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: 8,
          padding: '12px 16px', marginBottom: 16, color: '#991b1b', fontSize: 13,
          display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0,
        }}>
          <span style={{ fontWeight: 600 }}>CRM Load Error:</span> {loadError}
          <button onClick={() => { loadData(); loadExtended(); }} style={{
            marginLeft: 'auto', background: '#dc2626', color: '#fff', border: 'none',
            borderRadius: 4, padding: '4px 12px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
          }}>Retry</button>
        </div>
      )}

      {/* KPIs (sticky) */}
      {summary && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap', flexShrink: 0 }}>
          <KPI icon={Users} label="Contacts" value={summary.totalContacts} color="#2E8B98" />
          <KPI icon={MessageSquare} label="Interactions (7d)" value={summary.interactionsThisWeek} color="#62BB46" />
          <KPI icon={CheckSquare} label="Open Tasks" value={summary.openTasks} color="#F26522" sub={summary.overdueTasks > 0 ? `${summary.overdueTasks} overdue` : undefined} />
          <KPI icon={Zap} label="Active Workflows" value={workflows.filter(w => w.is_active).length} color="#256F7A" sub={`${workflowRuns.filter(r => r.status === 'running').length} running`} />
          <KPI icon={Tag} label="Tags" value={tags.length} color="#2E8B98" />
        </div>
      )}

      {/* Tabs (sticky) — grouped 7-category nav replaces the 49-tab flat row */}
      <div style={{ flexShrink: 0, marginBottom: 16 }}>
        <GroupedTabNav
          groups={GROUPS}
          activeTab={tab}
          /* ⛔ NOT `setTab` — Card P111. See changeSectionTab above: a bare
             setTab here is the whole B1 defect, because the open-contact
             overlay below outranks every tab body. The red arm of
             tests/react/CrmNavTabWithContactOpen.test.js is exactly this
             line reverted. */
          onTabChange={changeSectionTab}
          neuShadow={neuShadow}
          neuShadowInset={neuShadowInset}
        />
      </div>

      {/* Scrollable Content Area.
          ⚠ THIS is the element that scrolls (the page root is overflow:hidden),
          so it carries listScrollRef — the list scroll memory above reads and
          writes THIS node. Moving the ref to any other element silently breaks
          scroll restoration; CrmListScrollTarget.test.js asserts the pairing. */}
      <div ref={listScrollRef} style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {/* ═══ CONTACT DETAIL VIEW (full-screen overlay) ═══ */}
        {/* Task #71: Workspace tab bar (Manny P1). Renders when 1+ tabs open;
            sits above ContactDetailView so the rep can switch contexts
            without losing position. Tabs persist to localStorage per-user. */}
        <WorkspaceTabBar
          tabs={workspace.tabs}
          activeId={workspace.activeId}
          onActivate={activateContactTab}
          onClose={closeContactTab}
          onCloseAll={closeAllTabs}
        />
        {/* ⚠ #65 PHASE C3 — the open contact is ONE global Back step, so
            Back / Alt+← / the history dropdown / Android hardware Back all land
            on the list instead of unwinding out of the CRM. See
            ./crm/ContactWorkspaceNavStep.js for why `key` is load-bearing (the
            workspace holds several contacts open, and the obvious
            "is a contact open" predicate strands one of them). Renders nothing. */}
        {selectedContactId != null && (
          <ContactWorkspaceNavStep
            key={selectedContactId}
            contactId={selectedContactId}
            onClose={closeContactTab}
          />
        )}
        {selectedContactId && (
          <ContactDetailView
            contactId={selectedContactId}
            onBack={() => closeContactTab(selectedContactId)}
            /*
             * Card P100: the header's "Open patient chart" button. The jump
             * lives HERE because ContactDetailView may not touch
             * sessionStorage (tests/react/CrmUrlPlaceWiring) — an unsaved
             * note draft must never be persisted from that file.
             */
            canCreatePatient={canCreatePatient}
            canLinkPatient={canLinkPatient}
            canUnlinkPatient={canUnlinkPatient}
            onOpenPatient={(patientId) => {
              try { sessionStorage.setItem('cowork.patients.openPatient', String(patientId || '')); }
              catch { /* sessionStorage unavailable — still navigate */ }
              if (typeof onNavigate === 'function') onNavigate('patients');
            }}
            allUsers={allUsers}
            canUpdate={canUpdate}
            // Open the global Send Message modal directly from the contact
            // detail page's SMS / Email buttons. Without these props passed
            // through, the buttons up there only navigated to the
            // Conversations sub-tab — confusing for users trying to compose
            // a new message with the AI Suggest helper.
            setShowSendModal={setShowSendModal}
            setSendChannel={setSendChannel}
            setSendPrefill={setSendPrefill}
            setSendResult={setSendResult}
            onContactUpdated={loadData}
            // Pass user so ContactDetailView can derive physician:read /
            // physician:write / physician:sign for the new Physician tab.
            user={user}
            // Optional deep-link from the Physician Dashboard — opens the
            // contact on the specified sub-tab instead of the default
            // Profile tab. One-shot via sessionStorage handoff.
            initialTab={pendingTabOnOpen}
            onInitialTabConsumed={() => setPendingTabOnOpen(null)}
          />
        )}

        {/* ═══ TAB CONTENT ═══ */}
        {/* TabErrorBoundary wraps every tab. key={tab} remounts the
            boundary on tab switch so prior errors don't carry over. A
            crash in any one tab shows the fallback inside the boundary
            and leaves the surrounding chrome (nav, header, modals)
            working so the user can navigate away. Each tab still runs
            normally on the happy path. */}
        <TabErrorBoundary key={tab} tabName={tab}>
        {/* ⛔ One drawer, one host — see the note by drawerPreview. */}
        {drawerPreview && (
          <ContactDrawer
            contactId={drawerPreview.contactId}
            user={user}
            allUsers={allUsers}
            opportunity={drawerPreview.opportunity || null}
            pipelines={pipelines}
            onClose={() => setDrawerPreview(null)}
            onFullProfile={(id) => { setDrawerPreview(null); openContactTab(id); }}
            onStageMoved={onDrawerStageMoved}
          />
        )}
        {!selectedContactId && tab === 'my_day' && <MyDayTab user={user} onNavigate={setTab} onAppNavigate={onNavigate} onOpenContact={openContactTab} />}
        {!selectedContactId && tab === 'pipeline' && <PipelineTab {...sharedProps} />}
        {!selectedContactId && tab === 'daily_hit_list' && <DailyHitListTab {...sharedProps} />}
        {!selectedContactId && tab === 'opportunities' && <OpportunitiesTab {...sharedProps} />}
        {/* List-state preservation (Daniel, 2026-06-10): the Contacts tab
            stays mounted but hidden while a contact record is open, so the
            search term, filter bar state, and rendered list survive the
            round trip. Scroll restoration is handled above. */}
        {tab === 'contacts' && (
          <div style={{ display: selectedContactId ? 'none' : undefined }}>
            <ContactsTab {...sharedProps} />
          </div>
        )}
        {!selectedContactId && tab === 'users' && <UsersTab {...sharedProps} />}
        {!selectedContactId && tab === 'interactions' && <InteractionsTab {...sharedProps} />}
        {!selectedContactId && tab === 'tasks' && <TasksTab {...sharedProps} />}
        {!selectedContactId && tab === 'referrals' && <ReferralsTab {...sharedProps} />}
        {!selectedContactId && tab === 'research' && <ResearchTab />}
        {!selectedContactId && (tab === 'tags' || tab === 'fields' || tab === 'workflows') && <SettingsGroup {...sharedProps} />}
        {!selectedContactId && tab === 'templates' && <TemplatesTab {...sharedProps} />}
        {!selectedContactId && (tab === 'scorecard' || tab === 'escalations' || tab === 'comp') && <SOPGroup {...sharedProps} />}
        {!selectedContactId && tab === 'lead_score' && <LeadScoreTab {...sharedProps} />}
        {!selectedContactId && tab === 'comms' && <CommsTab {...sharedProps} />}
        {!selectedContactId && tab === 'api_settings' && <ApiSettingsTab {...sharedProps} />}
        {!selectedContactId && tab === 'automation' && <AutomationTab {...sharedProps} />}
        {!selectedContactId && tab === 'lead_assignment' && <LeadAssignmentTab {...sharedProps} />}
        {!selectedContactId && tab === 'sales_dashboard' && <SalesDashboardTab {...sharedProps} />}
        {!selectedContactId && tab === 'marketing_dashboard' && <MarketingDashboardTab {...sharedProps} />}
        {!selectedContactId && tab === 'operations_dashboard' && <OperationsDashboardTab {...sharedProps} />}
        {!selectedContactId && tab === 'sequences' && <SequencesTab {...sharedProps} />}
        {!selectedContactId && tab === 'analytics' && <AnalyticsTab {...sharedProps} />}
        {!selectedContactId && tab === 'timeline_tab' && <TimelineTab {...sharedProps} />}
        {!selectedContactId && tab === 'campaigns' && <CampaignsTab {...sharedProps} />}
        {!selectedContactId && tab === 'docs' && <DocsTab {...sharedProps} />}
        {!selectedContactId && tab === 'calendar' && <CalendarTab {...sharedProps} />}
        {!selectedContactId && tab === 'ops_calendar' && <OperationsCalendarTab {...sharedProps} />}
        {!selectedContactId && tab === 'filters' && <FiltersTab {...sharedProps} />}
        {!selectedContactId && tab === 'bulk' && <BulkOpsTab {...sharedProps} />}
        {!selectedContactId && tab === 'duplicates' && <DuplicatesTab {...sharedProps} />}
        {/* ⚠ setTab is passed EXPLICITLY, not via sharedProps. SopTab's six Quick
            Links are its only consumer, and every one of them was dead because
            sharedProps carries no setTab (inventory 2026-10-08 §10.1) — the
            click threw and nothing happened. Same shape as MyDayTab's
            onNavigate={setTab} above. */}
        {!selectedContactId && tab === 'sop' && <SopTab {...sharedProps} setTab={setTab} />}
        {!selectedContactId && tab === 'setup' && <SetupTab {...sharedProps} />}
        {!selectedContactId && tab === 'payments' && <PaymentsTab {...sharedProps} />}
        {!selectedContactId && tab === 'invoices' && <InvoicesTab {...sharedProps} />}
        {!selectedContactId && tab === 'catalog' && <CatalogAdminTab user={user} />}
        {!selectedContactId && tab === 'reputation' && <ReputationTab {...sharedProps} />}
        {!selectedContactId && tab === 'phone_system' && <PhoneSystemTab {...sharedProps} />}
        {!selectedContactId && tab === 'signatures' && <SignaturesTab {...sharedProps} />}
        {!selectedContactId && tab === 'funnels' && <FunnelsTab {...sharedProps} />}
        {!selectedContactId && tab === 'memberships' && <MembershipsTab {...sharedProps} />}
        {!selectedContactId && tab === 'webhooks' && <WebhooksTab {...sharedProps} />}
        {!selectedContactId && tab === 'social' && <SocialPlannerTab {...sharedProps} />}
        {!selectedContactId && tab === 'medical_intel' && <MedicalIntelligenceTab {...sharedProps} />}
        {!selectedContactId && tab === 'ai_assistant' && <AIAssistantTab {...sharedProps} />}
        {!selectedContactId && tab === 'import' && <CRMImportPage user={sharedProps.user || {}} />}
        {/* Competitive Intel accessible from sidebar → Intelligence → Competitive Intel */}
        </TabErrorBoundary>

        {/* ═══ MODALS ═══ */}
        {showAddContact && <AddContactModal users={allUsers} onClose={() => setShowAddContact(false)} onCreated={() => { setShowAddContact(false); loadData(); }} />}
        {showLogInteraction && <LogInteractionModal contacts={contacts} onClose={() => setShowLogInteraction(false)} onCreated={() => { setShowLogInteraction(false); loadData(); }} />}
        {showAddTask && (
          <AddTaskModal
            contacts={contacts}
            users={allUsers}
            onClose={() => setShowAddTask(false)}
            onCreated={() => {
              setShowAddTask(false);
              loadData();
              // ⇡ refreshes this page's `tasks`; the line below is what makes
              // the new task appear in the Tasks TAB, which owns its own list.
              setTaskListVersion((v) => v + 1);
            }}
          />
        )}
        {showEditContact && editContact && <EditContactModal contact={editContact} users={allUsers} onClose={() => { setShowEditContact(false); setEditContact(null); }} onSaved={() => { setShowEditContact(false); setEditContact(null); loadData(); }} />}

        {/* ═══ SEND MESSAGE MODAL (global — accessible from any tab) ═══ */}
        {showSendModal && tab !== 'comms' && (
          <SendMessageModal
            contacts={contacts} sendChannel={sendChannel} setSendChannel={setSendChannel}
            sendPrefill={sendPrefill} setSendPrefill={setSendPrefill}
            sendResult={sendResult} setSendResult={setSendResult}
            onClose={() => { setShowSendModal(false); setSendPrefill(null); }}
            onSent={() => { loadExtended(); }}
          />
        )}

        {/* ═══ BULK SEND MODAL ═══ */}
        {showBulkSend && (
          <BulkSendModal
            onClose={() => setShowBulkSend(false)}
            onSent={() => { loadExtended(); }}
          />
        )}
      </div>
    </div>
  );
}

export default CRMPage;
// Force frontend redeploy after lockfile-sync commit (2026-06-06)
